import { useState, useEffect, useCallback, useRef } from 'react'
import { format } from 'date-fns'
import friendsApi, { Friend, PendingRequest, SentRequest, InviteCodeResponse, LeaderboardResponse } from '@/api/friends'
import type { ApiError } from '@/services/api-client'

// Hook to get all friends
export function useFriends() {
  const [data, setData] = useState<Friend[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchFriends = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const friends = await friendsApi.getFriends()
      setData(friends)
    } catch (err) {
      const apiError = err as ApiError
      setError(new Error(apiError.message || 'Failed to fetch friends'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchFriends()
  }, [fetchFriends])

  return { data, isLoading, error, refetch: fetchFriends }
}

// Hook to get pending friend requests
export function usePendingRequests() {
  const [data, setData] = useState<PendingRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchRequests = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const requests = await friendsApi.getPendingRequests()
      setData(requests)
    } catch (err) {
      const apiError = err as ApiError
      setError(new Error(apiError.message || 'Failed to fetch pending requests'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  return { data, isLoading, error, refetch: fetchRequests }
}

// Hook to get sent friend requests
export function useSentRequests() {
  const [data, setData] = useState<SentRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchRequests = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const requests = await friendsApi.getSentRequests()
      setData(requests)
    } catch (err) {
      const apiError = err as ApiError
      setError(new Error(apiError.message || 'Failed to fetch sent requests'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  return { data, isLoading, error, refetch: fetchRequests }
}

// Hook to get user's invite code
export function useInviteCode() {
  const [data, setData] = useState<InviteCodeResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchInviteCode = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const inviteData = await friendsApi.getInviteCode()
      setData(inviteData)
    } catch (err) {
      const apiError = err as ApiError
      setError(new Error(apiError.message || 'Failed to fetch invite code'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchInviteCode()
  }, [fetchInviteCode])

  return { data, isLoading, error, refetch: fetchInviteCode }
}

// Hook to send a friend request
export function useSendFriendRequest() {
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutate = useCallback(async (friendId: string) => {
    try {
      setIsPending(true)
      setError(null)
      await friendsApi.sendFriendRequest(friendId)
      // Optionally refresh friends/requests data here
      window.location.reload() // Simple refresh for now
    } catch (err) {
      const apiError = err as ApiError
      setError(new Error(apiError.message || 'Failed to send friend request'))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [])

  return { mutate, isPending, error }
}

// Hook to accept a friend request
export function useAcceptFriendRequest() {
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutate = useCallback(async (requesterId: string) => {
    try {
      setIsPending(true)
      setError(null)
      await friendsApi.acceptFriendRequest(requesterId)
      // Optionally refresh friends/requests data here
      window.location.reload() // Simple refresh for now
    } catch (err) {
      const apiError = err as ApiError
      setError(new Error(apiError.message || 'Failed to accept friend request'))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [])

  return { mutate, isPending, error }
}

// Hook to reject a friend request
export function useRejectFriendRequest() {
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutate = useCallback(async (requesterId: string) => {
    try {
      setIsPending(true)
      setError(null)
      await friendsApi.rejectFriendRequest(requesterId)
      // Optionally refresh friends/requests data here
      window.location.reload() // Simple refresh for now
    } catch (err) {
      const apiError = err as ApiError
      setError(new Error(apiError.message || 'Failed to reject friend request'))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [])

  return { mutate, isPending, error }
}

// Hook to remove a friend
export function useRemoveFriend() {
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutate = useCallback(async (friendId: string) => {
    try {
      setIsPending(true)
      setError(null)
      await friendsApi.removeFriend(friendId)
      // Optionally refresh friends data here
      window.location.reload() // Simple refresh for now
    } catch (err) {
      const apiError = err as ApiError
      setError(new Error(apiError.message || 'Failed to remove friend'))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [])

  return { mutate, isPending, error }
}

// Hook to block a user
export function useBlockUser() {
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutate = useCallback(async (userId: string) => {
    try {
      setIsPending(true)
      setError(null)
      await friendsApi.blockUser(userId)
      // Optionally refresh friends data here
      window.location.reload() // Simple refresh for now
    } catch (err) {
      const apiError = err as ApiError
      setError(new Error(apiError.message || 'Failed to block user'))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [])

  return { mutate, isPending, error }
}

// Hook to refresh friends data
export function useRefreshFriendsData() {
  const refreshData = useCallback(async () => {
    // Simple approach: reload the page
    window.location.reload()
  }, [])

  return refreshData
}

// Simple in-memory cache for leaderboard data with TTL
interface CacheEntry {
  data: LeaderboardResponse
  timestamp: number
  ttl: number
}

const leaderboardCache = new Map<string, CacheEntry>()

// Helper function to check if cache entry is valid
function isCacheValid(entry: CacheEntry): boolean {
  return Date.now() - entry.timestamp < entry.ttl
}

// Helper function to get cache key
function getCacheKey(date: string, gameId: string): string {
  return `leaderboard:${date}:${gameId}`
}

// Helper function to determine TTL based on date
function getTTL(date: string): number {
  const today = format(new Date(), 'yyyy-MM-dd')
  // 2 minutes for today's data (to feel real-time), 30 minutes for historical data
  return date === today ? 2 * 60 * 1000 : 30 * 60 * 1000
}

// Hook to get friends leaderboard with caching
export function useLeaderboard(date: string, gameId: string) {
  const [data, setData] = useState<LeaderboardResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const fetchLeaderboard = useCallback(async (skipCache = false) => {
    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      setIsLoading(true)
      setError(null)

      // Check cache first (unless skipped)
      const cacheKey = getCacheKey(date, gameId)
      if (!skipCache && leaderboardCache.has(cacheKey)) {
        const cacheEntry = leaderboardCache.get(cacheKey)!
        if (isCacheValid(cacheEntry)) {
          setData(cacheEntry.data)
          setIsLoading(false)
          return
        } else {
          // Remove expired cache entry
          leaderboardCache.delete(cacheKey)
        }
      }

      // Fetch from API
      const leaderboard = await friendsApi.getLeaderboard(date, gameId)
      
      // Only update state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setData(leaderboard)
        
        // Cache the result
        const ttl = getTTL(date)
        leaderboardCache.set(cacheKey, {
          data: leaderboard,
          timestamp: Date.now(),
          ttl
        })
      }
    } catch (err) {
      if (!abortController.signal.aborted) {
        const apiError = err as ApiError
        setError(new Error(apiError.message || 'Failed to fetch leaderboard'))
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false)
      }
    }
  }, [date, gameId])

  const refetch = useCallback(() => {
    return fetchLeaderboard(true) // Skip cache on manual refetch
  }, [fetchLeaderboard])

  useEffect(() => {
    fetchLeaderboard()

    // Cleanup function to abort request on unmount or dependency change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [fetchLeaderboard])

  return { data, isLoading, error, refetch }
}

// Hook to clear leaderboard cache (useful for testing or manual cache invalidation)
export function useClearLeaderboardCache() {
  const clearCache = useCallback(() => {
    leaderboardCache.clear()
  }, [])

  return clearCache
}